import { useState } from 'react';
import { enableUserSkill } from '@api/backoffice';
import { axiosErrorMessage } from '@api/errors';
import {
  accountExpiryBounds,
  expiryDateInputToIso,
  isExpiryDateInBounds,
  MAX_ACCOUNT_EXPIRY_YEARS,
  toExpiryDateInputValue,
} from '@utils/accountExpiry';
import {
  ActionButton,
  ButtonRow,
  CardBody,
  DialogContent,
  DialogOverlay,
  DialogTitle,
  FormField,
  FormInput,
  FormLabel,
  SmallText,
} from '@components/Backoffice/BackofficeLayout.styled';
import type { UserSkillAccessDialogProps } from './types';

const INPUT_ID = 'backoffice-skill-expiry-date';

function UserSkillAccessDialog({
  userId,
  userEmail,
  skill,
  onClose,
  onSave,
  onError,
}: UserSkillAccessDialogProps) {
  const bounds = accountExpiryBounds();
  const [expiryDate, setExpiryDate] = useState(() => {
    const current = toExpiryDateInputValue(skill.expiresAt);
    return isExpiryDateInBounds(current, bounds) ? current : '';
  });
  const [saving, setSaving] = useState(false);
  const isEdit = skill.enabled;
  const submitLabel = isEdit ? 'Save' : 'Grant';
  const validDate = expiryDate === '' || isExpiryDateInBounds(expiryDate, bounds);

  const handleSave = async () => {
    setSaving(true);
    try {
      await enableUserSkill(
        userId,
        skill.skillId,
        expiryDate ? expiryDateInputToIso(expiryDate) : null,
      );
      onSave();
      onClose();
    } catch (err) {
      onError(axiosErrorMessage(err, `Failed to update ${skill.displayName}`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogOverlay onClick={saving ? undefined : onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>
          {isEdit ? `Change ${skill.displayName} expiry` : `Grant ${skill.displayName}?`}
        </DialogTitle>
        {!isEdit && (
          <CardBody>
            Grant <strong>{skill.displayName}</strong> access for <strong>{userEmail}</strong>.
          </CardBody>
        )}
        <FormField>
          <FormLabel htmlFor={INPUT_ID}>Expiry Date</FormLabel>
          <FormInput
            id={INPUT_ID}
            type="date"
            min={bounds.min}
            max={bounds.max}
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
          <SmallText>
            Optional. Up to {MAX_ACCOUNT_EXPIRY_YEARS} years from today. Leave empty for no expiry.
          </SmallText>
        </FormField>
        <ButtonRow>
          {expiryDate && (
            <ActionButton $variant="ghost" onClick={() => setExpiryDate('')} disabled={saving}>
              No Expiry
            </ActionButton>
          )}
          <ActionButton $variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </ActionButton>
          <ActionButton $variant="primary" onClick={handleSave} disabled={saving || !validDate}>
            {saving ? 'Saving...' : submitLabel}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
}

export default UserSkillAccessDialog;
